import React, { useEffect, useState } from 'react';
import { Alert, Col, Divider, Row } from 'antd';
import { useAtom } from 'jotai';
import { fullSensitivityConfigAtom } from '../atoms';
import { StyledInput, StyledUnit, StyledConfigLabel, StyledInputWrapper } from './styled';
import { getMetricDisplayName, getMetricUnitByKey } from '@src/utils/metrics';
import { useSelector } from 'react-redux';
import { isAdminRoleSelector, aiAlertFusionVsiSelector } from '@src/store/common/selectors';
import { checkErrors } from '../errorUtils';

const AdSensitivityRow = ({ sensitivity }) => {
  const [sensitivityDetails, setSensitivityDetails] = useAtom(fullSensitivityConfigAtom);
  const [errorMsg, setErrorMsg] = useState('');
  const isAdminUser = useSelector(isAdminRoleSelector);
  const fusionVsiEnabled = useSelector(aiAlertFusionVsiSelector);

  const style: React.CSSProperties = {
    padding: '0 0 15px 0',
    fontWeight: 500,
    fontSize: 14,
    display: 'flex',
  };

  const onChangeKey =
    ({ key }) =>
    (value) => {
      setSensitivityDetails((prev) => {
        return prev.map((sensitivityConfig) => {
          if (sensitivityConfig.metricKey === sensitivity.metricKey) {
            return { ...sensitivityConfig, [key]: value };
          }
          return sensitivityConfig;
        });
      });
    };

  useEffect(() => {
    setErrorMsg(checkErrors(sensitivityDetails.find((m) => m.metricKey === sensitivity.metricKey)));
  }, [sensitivityDetails]);

  const metricName = getMetricDisplayName({
    metricKey: sensitivity.metricKey,
    context: 'EI',
  });

  const metricUnit = getMetricUnitByKey({
    metricKey: sensitivity.metricKey,
    context: 'EI',
  });

  return (
    <>
      <Divider orientation="left" orientationMargin="0" style={{ margin: '10px 0' }}>
        {metricName}
      </Divider>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col className="gutter-row" span={12}>
          <StyledConfigLabel style={style}>Threshold exceeds</StyledConfigLabel>
        </Col>

        <Col className="gutter-row" span={12}>
          <div style={style}>
            <StyledInput
              disabled={!fusionVsiEnabled || !isAdminUser}
              min={1}
              max={100}
              onChange={onChangeKey({ key: 'upperThreshold' })}
              value={sensitivity.upperThreshold}
            />
            <StyledUnit>{metricUnit}</StyledUnit>
          </div>
        </Col>
      </Row>

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col className="gutter-row" span={12}>
          <StyledConfigLabel style={style}>Impacted devices exceeds</StyledConfigLabel>
        </Col>

        <Col className="gutter-row" span={12}>
          <div style={style}>
            <StyledInputWrapper>
              <StyledInput
                disabled={!fusionVsiEnabled || !isAdminUser}
                min={10}
                max={1000000}
                onChange={onChangeKey({ key: 'impactedUnique' })}
                value={sensitivity.impactedUnique}
              />
              <StyledUnit>#</StyledUnit>
            </StyledInputWrapper>
          </div>
        </Col>
      </Row>
      {errorMsg ? <Alert message={errorMsg} type={'error'} /> : null}
    </>
  );
};

export default React.memo(AdSensitivityRow);
